/**
 * Navigation from the contract list to the contract detail (contract §9:
 * `/contrats/:idNpol?id_per=…`).
 *
 * `WS2_EA_FIC_POL` returns neither the insured id nor the insured name, so
 * the list row carries them: `ID_PER` as the `id_per` query parameter (the
 * « Assuré » tab's `id_per_ass`) and `NOM2_PER` / `LIB_PROD` / `NUM_POL` as
 * router state for the detail header.
 */

import { extranetPath } from '../../navigation';
import { formatText } from '../../utils/format';

/** Query parameter of the detail route carrying the insured id. */
export const CONTRACT_ID_PER_PARAM = 'id_per';

/** Router state handed from the list row to the detail header. */
export interface ContractRouteState {
  /** Insured name (`NOM2_PER`) — the detail title. */
  assure?: string | null;
  /** Product (`LIB_PROD`). */
  produit?: string | null;
  /** Contract number (`NUM_POL`). */
  numero?: string | null;
}

/** Trimmed text of an unknown row value, `null` when blank / not a scalar. */
function scalarText(value: unknown): string | null {
  if (typeof value !== 'string' && typeof value !== 'number') return null;
  const text = formatText(value);
  return text === '' ? null : text;
}

/** Absolute path of a contract detail (`id_per` only when known). */
export function contractDetailPath(
  tenantSlug: string | undefined,
  idNpol: string | number,
  idPer?: string | number | null,
): string {
  const base = extranetPath(tenantSlug, 'contrats', idNpol);
  const per = idPer === null || idPer === undefined ? '' : formatText(idPer);
  return per === '' ? base : `${base}?${CONTRACT_ID_PER_PARAM}=${encodeURIComponent(per)}`;
}

/** Where a raw `WS2_EA_TAB_POLICES` row opens; `null` without `ID_NPOL`. */
export function contractDetailTarget(
  tenantSlug: string | undefined,
  row: Record<string, unknown>,
): { path: string; state: ContractRouteState } | null {
  const idNpol = scalarText(row.ID_NPOL);
  if (idNpol === null) return null;
  return {
    path: contractDetailPath(tenantSlug, idNpol, scalarText(row.ID_PER)),
    state: {
      assure: scalarText(row.NOM2_PER),
      produit: scalarText(row.LIB_PROD),
      numero: scalarText(row.NUM_POL),
    },
  };
}

/** Read the {@link ContractRouteState} off `location.state` (defensive). */
export function readContractRouteState(state: unknown): ContractRouteState {
  if (typeof state !== 'object' || state === null) return {};
  const record = state as Record<string, unknown>;
  return {
    assure: scalarText(record.assure),
    produit: scalarText(record.produit),
    numero: scalarText(record.numero),
  };
}
